import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { emailReportsAPI } from '../services/api';

const DIAS = ['Domingo','Lunes','Martes','Miercoles','Jueves','Viernes','Sabado'];

const EmailReportsPage = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [activeTab, setActiveTab] = useState('config');

  const load = useCallback(async () => {
    if (!user?.establecimiento_id) return;
    setLoading(true);
    try {
      const [cfgRes, histRes, prevRes] = await Promise.allSettled([
        emailReportsAPI.getConfig(user.establecimiento_id),
        emailReportsAPI.historial(user.establecimiento_id),
        emailReportsAPI.preview(user.establecimiento_id),
      ]);
      if (cfgRes.status === 'fulfilled') setConfig(cfgRes.value.data);
      if (histRes.status === 'fulfilled') setHistorial(histRes.value.data.historial || []);
      if (prevRes.status === 'fulfilled') setPreview(prevRes.value.data);
    } catch { /**/ } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleSaveConfig = async () => {
    if (!config) return;
    try {
      await emailReportsAPI.saveConfig(user.establecimiento_id, config);
      alert('Configuracion guardada');
    } catch { alert('Error al guardar'); }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) return;
    setConfig(prev => ({ ...prev, destinatarios: [...(prev.destinatarios || []), newEmail] }));
    setNewEmail('');
  };

  const handleRemoveEmail = (idx) => {
    setConfig(prev => ({ ...prev, destinatarios: prev.destinatarios.filter((_, i) => i !== idx) }));
  };

  const handleSendNow = async () => {
    if (!sendEmail) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await emailReportsAPI.enviar(user.establecimiento_id, sendEmail);
      setSendResult(res.data);
      load();
    } catch (e) { setSendResult({ status: 'error', message: e.response?.data?.detail || 'Error' }); }
    finally { setSending(false); }
  };

  const tabs = [
    { id: 'config', label: 'Configuracion' },
    { id: 'preview', label: 'Vista Previa' },
    { id: 'historial', label: 'Historial' },
    { id: 'enviar', label: 'Enviar Ahora' },
  ];

  if (loading) return <div className="p-6 flex justify-center"><div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="email-reports-page">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Reportes por Email</h2>
        <p className="text-sm text-gray-500">Configura envios automaticos del resumen semanal del rodeo</p>
      </div>

      <div className="flex bg-gray-100 rounded-lg p-1 gap-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${activeTab === t.id ? 'bg-white text-green-700 font-semibold shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`email-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {/* CONFIG */}
      {activeTab === 'config' && config && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5" data-testid="email-config-section">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-800">Envio Automatico</h3>
              <p className="text-xs text-gray-400">Activa para recibir reportes periodicamente</p>
            </div>
            <button onClick={() => setConfig(p => ({ ...p, activo: !p.activo }))}
              className={`w-12 h-6 rounded-full transition-colors relative ${config.activo ? 'bg-green-500' : 'bg-gray-300'}`}
              data-testid="toggle-active">
              <span className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${config.activo ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Frecuencia</label>
              <select value={config.frecuencia} onChange={e => setConfig(p => ({ ...p, frecuencia: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="frecuencia-select">
                <option value="semanal">Semanal</option>
                <option value="quincenal">Quincenal</option>
                <option value="mensual">Mensual</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Dia de envio</label>
              <select value={config.dia_semana} onChange={e => setConfig(p => ({ ...p, dia_semana: Number(e.target.value) }))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="dia-select">
                {DIAS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Destinatarios</label>
            <div className="flex gap-2 mb-2">
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@ejemplo.com" className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="add-email-input" />
              <button onClick={handleAddEmail} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700" data-testid="add-email-btn">Agregar</button>
            </div>
            <div className="space-y-1">
              {(config.destinatarios || []).map((em, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                  <span>{em}</span>
                  <button onClick={() => handleRemoveEmail(i)} className="text-red-500 hover:text-red-700 text-xs">Eliminar</button>
                </div>
              ))}
              {(!config.destinatarios || config.destinatarios.length === 0) && <p className="text-xs text-gray-400">Sin destinatarios configurados</p>}
            </div>
          </div>

          <button onClick={handleSaveConfig} className="w-full px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 font-medium" data-testid="save-config-btn">Guardar Configuracion</button>
        </div>
      )}

      {/* PREVIEW */}
      {activeTab === 'preview' && preview && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="email-preview-section">
          <h3 className="font-semibold text-gray-800 mb-4">Vista Previa del Email</h3>
          <div className="border rounded-lg overflow-hidden">
            <div dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-sm">
            <div className="bg-green-50 rounded-lg p-3"><strong className="text-green-700">{preview.data?.stock?.total || 0}</strong><br /><span className="text-gray-500 text-xs">Total</span></div>
            <div className="bg-pink-50 rounded-lg p-3"><strong className="text-pink-700">{preview.data?.kpis?.tasa_prenez || 0}%</strong><br /><span className="text-gray-500 text-xs">Prenez</span></div>
            <div className="bg-blue-50 rounded-lg p-3"><strong className="text-blue-700">{preview.data?.kpis?.ep_promedio || 0}%</strong><br /><span className="text-gray-500 text-xs">EP</span></div>
            <div className="bg-yellow-50 rounded-lg p-3"><strong className="text-yellow-700">{preview.data?.kpis?.peso_promedio || 0} kg</strong><br /><span className="text-gray-500 text-xs">Peso</span></div>
          </div>
        </div>
      )}

      {/* HISTORIAL */}
      {activeTab === 'historial' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="email-historial-section">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b"><th className="text-left px-4 py-3 font-medium text-gray-600">Fecha</th><th className="text-left px-4 py-3 font-medium text-gray-600">Email</th><th className="text-center px-4 py-3 font-medium text-gray-600">Estado</th></tr></thead>
            <tbody>
              {historial.length === 0 ? (
                <tr><td colSpan="3" className="text-center py-8 text-gray-400">Sin envios registrados</td></tr>
              ) : historial.map((h, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3">{h.fecha?.slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-4 py-3">{h.email}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${h.resultado === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{h.resultado === 'success' ? 'Enviado' : 'Error'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ENVIAR AHORA */}
      {activeTab === 'enviar' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4" data-testid="email-send-section">
          <h3 className="font-semibold text-gray-800">Enviar Reporte Ahora</h3>
          <div className="flex gap-2">
            <input type="email" value={sendEmail} onChange={e => setSendEmail(e.target.value)} placeholder="Destinatario" className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="send-email-input" />
            <button onClick={handleSendNow} disabled={sending || !sendEmail} className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50" data-testid="send-now-btn">
              {sending ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
          {sendResult && (
            <div className={`p-3 rounded-lg text-sm ${sendResult.status === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="send-result">
              {sendResult.message}
            </div>
          )}
          <p className="text-xs text-gray-400">Nota: Para enviar emails, se necesita una clave RESEND_API_KEY configurada en el backend. En modo demo, el email se simula.</p>
        </div>
      )}
    </div>
  );
};

export default EmailReportsPage;
